import maplibregl, {
  type FilterSpecification,
  type GeoJSONSourceSpecification,
  type Map as MLMap,
} from 'maplibre-gl';
import { Protocol as PMTilesProtocol } from 'pmtiles';
import type {
  EngineAdapter,
  Layer,
  LayerKind,
  VectorExportFeature,
  ViewerCamera,
  ViewerHostContext,
} from '@geosoft/viewer-sdk';
import {
  declareCapabilities,
  type EngineCapabilities,
  type LayerRenderDiagnostics,
} from '@/engines/contract';
import {
  clearMapLibreHostHandle,
  setMapLibreHostHandle,
} from '@/engines/maplibre/hostHandle';
import {
  applyBasemap,
  loadPreferredBasemapId,
  savePreferredBasemapId,
  type BasemapId,
} from '@/engines/maplibre/basemaps';
import { resolveCanvasLightColor, resolveVectorFallbackColor } from '@/design/tokenResolver';
import { mercatorZoomForCameraHeight } from '@/engines/maplibre/overlayZoom';
import {
  catalogLayerIdFromMapLayerId,
  resolveFeatureIdFromRenderedFeature,
} from '@/engines/maplibre/maplibreVectorPick';
import {
  isContourPmtilesLayer,
  readDeclaredPmtilesSourceLayer,
  readPmtilesArchiveInfo,
  reconcilePmtilesSourceLayer,
} from '@/engines/pmtiles/sourceLayer';

const SUPPORTED: LayerKind[] = ['raster', 'vector'];

function isNonJsonVectorFeaturesUrl(url: string): boolean {
  try {
    const pathname = new URL(url, 'http://localhost').pathname.toLowerCase();
    return pathname.endsWith('.fgb')
      || pathname.endsWith('.parquet')
      || pathname.endsWith('.dxf');
  } catch {
    return /\.(fgb|parquet|dxf)(?:[?#]|$)/i.test(url);
  }
}

function layerUnsupportedReason(layer: Layer): string | undefined {
  const reason = layer.style?.viewerUnsupportedReason;
  return typeof reason === 'string' && reason.length > 0 ? reason : undefined;
}

type BboxFeatureCollection = {
  type: 'FeatureCollection';
  features: Array<{ geometry?: { coordinates?: unknown } | null }>;
};

const MAPLIBRE_CAPABILITIES: EngineCapabilities = declareCapabilities({
  engineId: 'maplibre',
  supportedLayerKinds: SUPPORTED,
  supportsTime: true,
  supportsTerrain: false,
  supports3DTiles: 'none',
  supportsInterleaving: ['deckgl'],
  defenceVariantSafe: true,
  pluginHostKinds: ['measurement', 'annotation', 'export'],
});

/** Register the `pmtiles://` protocol on MapLibre exactly once per process. */
let pmtilesProtocolRegistered = false;
export function ensurePmtilesProtocol(): void {
  if (pmtilesProtocolRegistered) return;
  const protocol = new PMTilesProtocol();
  maplibregl.addProtocol('pmtiles', protocol.tile);
  pmtilesProtocolRegistered = true;
}

/**
 * MapLibre adapter - base map + raster overlays + simple vector layers + PMTiles.
 *
 * deck.gl can be interleaved on the same canvas later; for now we ship the
 * map only and mount deck.gl as an overlay in DeckGLAdapter.
 */
/** Extend a lon/lat bbox by walking nested GeoJSON coordinate arrays. */
function extendBboxFromCoords(
  bbox: [number, number, number, number] | null,
  coords: unknown,
): [number, number, number, number] | null {
  if (coords === null || coords === undefined) return bbox;
  if (Array.isArray(coords) && coords.length >= 2 && typeof coords[0] === 'number') {
    const x = coords[0] as number;
    const y = coords[1] as number;
    if (!Number.isFinite(x) || !Number.isFinite(y)) return bbox;
    if (!bbox) return [x, y, x, y];
    return [
      Math.min(bbox[0], x),
      Math.min(bbox[1], y),
      Math.max(bbox[2], x),
      Math.max(bbox[3], y),
    ];
  }
  if (!Array.isArray(coords)) return bbox;
  let b = bbox;
  for (const c of coords) {
    b = extendBboxFromCoords(b, c);
  }
  return b;
}

/**
 * Convert a TiTiler XYZ tile template URL like
 *   `<base>/cog/tiles/WebMercatorQuad/{z}/{x}/{y}.png?url=...&bidx=...`
 * into the matching `tilejson.json` URL so the source can read the COG's
 * real bounds and min/max zoom.
 */
function deriveTitilerTileJsonUrl(tilesUrl: string, preferTileSize?: number): string | null {
  const match = tilesUrl.match(/^(.*?)\/cog\/tiles\/([^/]+)\/\{z\}\/\{x\}\/\{y\}(?:\.[a-zA-Z]+)?(\?.*)?$/);
  if (!match) return null;
  const [, base, tileMatrixSet, query] = match;
  const tilejson = `${base}/cog/${tileMatrixSet}/tilejson.json${query ?? ''}`;
  if (preferTileSize) {
    const sep = tilejson.includes('?') ? '&' : '?';
    if (!tilejson.includes('tilesize=')) return `${tilejson}${sep}tilesize=${preferTileSize}`;
  }
  return tilejson;
}

/**
 * TiTiler runs in Docker in local dev. When the embedded COG `url=` points to
 * `localhost`, TiTiler resolves that to the container itself and fails to read
 * converter artifacts. Rewrite to `host.docker.internal` so the same layer
 * payload works both in browser and containerized TiTiler.
 */
function normalizeTitilerSourceHost(tilesUrl: string): string {
  const match = /([?&]url=)([^&]+)/.exec(tilesUrl);
  if (!match || match.index === undefined) return tilesUrl;
  const [, prefix, encoded] = match;
  let source: URL;
  try {
    source = new URL(decodeURIComponent(encoded));
  } catch {
    return tilesUrl;
  }
  if (source.hostname !== 'localhost' && source.hostname !== '127.0.0.1') return tilesUrl;
  source.hostname = 'host.docker.internal';
  const rewritten = encodeURIComponent(source.toString());
  const start = match.index + prefix.length;
  const end = match.index + match[0].length;
  return `${tilesUrl.slice(0, start)}${rewritten}${tilesUrl.slice(end)}`;
}

function isLikelySingleBandRaster(layer: Layer): boolean {
  const family = String(layer.style?.['family'] ?? '').toLowerCase();
  const title = String(layer.style?.['title'] ?? layer.label ?? layer.id).toLowerCase();
  if (family === 'imagery' || title.includes('ortho') || title.includes('orthophoto')) return false;
  return (
    family === 'terrain'
    || family === 'landcover'
    || title.includes('dem')
    || title.includes('dtm')
    || title.includes('dsm')
    || title.includes('lulc')
    || title.includes('landcover')
  );
}

function normalizeRasterBands(tilesUrl: string, layer: Layer): string {
  if (!isLikelySingleBandRaster(layer)) return tilesUrl;
  try {
    const url = new URL(tilesUrl);
    url.searchParams.delete('bidx');
    url.searchParams.append('bidx', '1');
    return url.toString().replace(/%7B/g, '{').replace(/%7D/g, '}');
  } catch {
    return tilesUrl.replace(/&bidx=2&bidx=3/g, '');
  }
}

function bboxFromGeoJsonFeatureCollection(
  data: BboxFeatureCollection,
): [number, number, number, number] | null {
  let bbox: [number, number, number, number] | null = null;
  for (const f of data.features ?? []) {
    const g = f.geometry;
    if (!g?.coordinates) continue;
    bbox = extendBboxFromCoords(bbox, g.coordinates);
  }
  return bbox;
}

/**
 * Construction options for `MapLibreAdapter`.
 *
 * `vectorOverlayMode` is used when the adapter is mounted **alongside**
 * Giro3D (engine = 'giro3d') purely to render vector / PMTiles layers
 * as a 2D overlay on top of the unified Giro3D scene. In this mode:
 *
 *   - The MapLibre canvas is positioned absolutely on top of the
 *     Giro3D canvas with `pointer-events: none` so 3D OrbitControls
 *     keep working.
 *   - The MapLibre canvas background is transparent and no basemap
 *     is mounted — Giro3D already paints the imagery basemap via its
 *     own Map entity.
 *   - `moveend` does NOT emit `cameraChange` events so it never
 *     fights Giro3D's primary camera.
 *
 * Trade-off: vectors are rendered in 2D top-down map projection and do
 * NOT drape on the 3D terrain. Rotating Giro3D's 3D camera leaves
 * vectors flat in screen space. Proper 3D-draped vectors require a
 * Giro3D vector ColorLayer (Wave-3 scope).
 */
export interface MapLibreAdapterOptions {
  readonly vectorOverlayMode?: boolean;
}

export class MapLibreAdapter implements EngineAdapter {
  id = 'maplibre' as const;
  readonly capabilities: EngineCapabilities = MAPLIBRE_CAPABILITIES;
  private map: MLMap | null = null;
  private rasterLayerIds = new Set<string>();
  /**
   * Cache of the GeoJSON features held by each `vector` layer the
   * adapter knows about. Populated by `addVectorFromFeaturesEndpoint`;
   * consumed by `getVectorFeatures` so the WP-4 attribute table can
   * render rows without re-fetching.
   */
  private vectorFeatureCache = new Map<string, GeoJSON.Feature[]>();
  /** MapLibre style layer ids used by `queryRenderedFeatures` for vector picks. */
  private vectorPickLayerIds = new Set<string>();
  private containerClickHandler: ((event: MouseEvent) => void) | null = null;
  private hostContainer: HTMLElement | null = null;
  private overlayResizeObserver: ResizeObserver | null = null;
  private vectorOverlayMode: boolean;
  private currentBasemapId: BasemapId = loadPreferredBasemapId();
  private readonly canvasLight = resolveCanvasLightColor();
  private readonly vectorFallback = resolveVectorFallbackColor();
  private lastVectorPickProperties: Readonly<Record<string, unknown>> | null = null;
  private readonly layerHealth = new Map<string, 'ready' | 'loading' | 'failed' | 'unsupported'>();
  /**
   * Outcome of PMTiles `source-layer` reconciliation per layer, so the live
   * diagnostics (Layer Properties) can surface a producer-side mismatch:
   * what the scene item DECLARED vs what was APPLIED vs what the archive
   * actually CONTAINS. Populated in `resolvePmtilesSourceLayer`.
   */
  private readonly pmtilesSourceLayerResolution = new Map<
    string,
    { declared: string | null; applied: string | null; available: readonly string[] }
  >();

  constructor(options: MapLibreAdapterOptions = {}) {
    this.vectorOverlayMode = options.vectorOverlayMode === true;
  }

  supports(kind: LayerKind): boolean {
    // Vector-overlay mode (used when Giro3D is the primary engine and
    // MapLibre is mounted only to render 2D vector overlays) must NOT
    // claim raster support — the adapter intentionally has no basemap
    // in that mode, so a raster layer routed here would be silently
    // dropped and the operator would see a blank background. Refusing
    // raster here lets ViewerHost.pickEngine fall back to the primary
    // adapter (Giro3D) which renders OSM via its Map entity.
    if (this.vectorOverlayMode && kind === 'raster') return false;
    return SUPPORTED.includes(kind);
  }

  async mount(host: ViewerHostContext): Promise<void> {
    const overlay = this.vectorOverlayMode;
    this.hostContainer = host.container;
    this.map = new maplibregl.Map({
      container: host.container,
      style: {
        version: 8,
        sources: {},
        layers: overlay
          ? []
          : [
              {
                id: 'background',
                type: 'background',
                paint: { 'background-color': this.canvasLight },
              },
            ],
      },
      center: [55.27, 25.2],
      zoom: 9,
      maxZoom: 22,
      attributionControl: { compact: true },
      preserveDrawingBuffer: true,
    });

    setMapLibreHostHandle(host.container, this.map);

    if (!overlay) {
      this.map.addControl(new maplibregl.NavigationControl({ visualizePitch: true }), 'top-right');
      this.map.addControl(new maplibregl.ScaleControl({ maxWidth: 120 }), 'bottom-left');
      this.currentBasemapId = loadPreferredBasemapId();
    }

    if (!overlay) {
      // In overlay-mode the primary 3D camera lives on Giro3D; emitting
      // cameraChange from MapLibre would fight it. Skip the listener.
      this.map.on('moveend', () => {
        if (!this.map) return;
        const c = this.map.getCenter();
        host.emit({
          type: 'cameraChange',
          camera: { lonLat: [c.lng, c.lat], zoom: this.map.getZoom() },
        });
      });
    }

    await new Promise<void>((resolve) => {
      this.map!.once('load', () => resolve());
    });

    if (overlay) {
      // Vector-overlay mode: paint MapLibre canvas on top of Giro3D
      // with transparent background and pass-through pointer events.
      // The host container is shared with Giro3D, which mounts its own
      // canvas earlier; the MapLibre canvas is added later in the
      // sibling list, so it naturally sits above.
      //
      // Allow steep tilt so the overlay can follow oblique Giro3D poses
      // (syncOverlayCamera maps the 3D pitch into MapLibre's 0..maxPitch
      // range). Scoped to overlay mode so primary-engine UX is unchanged.
      try {
        this.map.setMaxPitch(85);
      } catch {
        /* older MapLibre without setMaxPitch — falls back to default cap */
      }
      const canvas = this.map.getCanvas();
      canvas.style.position = 'absolute';
      canvas.style.inset = '0';
      canvas.style.pointerEvents = 'none';
      canvas.style.background = 'transparent';
      canvas.style.zIndex = '10';
      const canvasParent = canvas.parentElement;
      if (canvasParent) {
        canvasParent.style.position = 'absolute';
        canvasParent.style.inset = '0';
        canvasParent.style.pointerEvents = 'none';
        canvasParent.style.background = 'transparent';
        canvasParent.style.zIndex = '10';
      }
      // The wrapper element MapLibre injects also needs the overlay
      // treatment so the canvas dimensions track the host.
      const wrapper = this.map.getContainer();
      wrapper.style.position = 'absolute';
      wrapper.style.inset = '0';
      wrapper.style.pointerEvents = 'none';
      wrapper.style.background = 'transparent';
      wrapper.style.zIndex = '10';
      this.map.resize();
      requestAnimationFrame(() => this.map?.resize());
      requestAnimationFrame(() => requestAnimationFrame(() => this.map?.resize()));
      if (typeof ResizeObserver !== 'undefined') {
        this.overlayResizeObserver = new ResizeObserver(() => {
          try {
            this.map?.resize();
          } catch {
            /* resize is best-effort */
          }
        });
        this.overlayResizeObserver.observe(host.container);
      }
      console.log('[MapLibreAdapter] mounted in vector-overlay mode (no basemap, pointer-events: none)');
      this.installVectorClickHandler(host);
    } else {
      // Apply the persisted basemap once the style is ready (primary mode only).
      try {
        applyBasemap(this.map!, this.currentBasemapId);
      } catch (err) {
        console.warn('[MapLibreAdapter] applyBasemap on mount failed', err);
      }
      this.map.on('click', (event) => {
        const hit = this.queryTopVectorFeature(event.point);
        if (hit) {
          host.emit({
            type: 'select',
            selection: {
              layerId: hit.catalogLayerId,
              featureId: hit.featureId,
              point: [event.lngLat.lng, event.lngLat.lat],
            },
          });
          return;
        }
        host.emit({
          type: 'select',
          selection: {
            layerId: '__map__',
            point: [event.lngLat.lng, event.lngLat.lat],
          },
        });
      });
    }
  }

  /** Active basemap id (persisted across reloads via localStorage). */
  getBasemapId(): BasemapId {
    return this.currentBasemapId;
  }

  /** Swap the basemap at runtime without disturbing user data layers. */
  setBasemap(id: BasemapId): void {
    if (!this.map) return;
    if (id === this.currentBasemapId) return;
    applyBasemap(this.map, id);
    this.currentBasemapId = id;
    savePreferredBasemapId(id);
  }

  async unmount(): Promise<void> {
    if (this.hostContainer && this.containerClickHandler) {
      this.hostContainer.removeEventListener('click', this.containerClickHandler, true);
    }
    this.containerClickHandler = null;
    this.hostContainer = null;
    this.overlayResizeObserver?.disconnect();
    this.overlayResizeObserver = null;
    const container = this.map?.getContainer();
    this.map?.remove();
    if (container) {
      clearMapLibreHostHandle(container);
    }
    this.map = null;
    this.rasterLayerIds.clear();
    this.vectorFeatureCache.clear();
    this.vectorPickLayerIds.clear();
    this.layerHealth.clear();
  }

  async addLayer(layer: Layer): Promise<void> {
    if (!this.map) throw new Error('MapLibreAdapter not mounted');
    this.layerHealth.set(layer.id, 'loading');
    if (!this.supports(layer.kind)) {
      this.layerHealth.set(layer.id, 'unsupported');
      return;
    }

    const unsupportedReason = layerUnsupportedReason(layer);
    if (unsupportedReason) {
      this.layerHealth.set(layer.id, 'unsupported');
      return;
    }

    const sourceId = `src-${layer.id}`;
    const layerId = `layer-${layer.id}`;

    // React dev strict-mode and scene sync retries can call addLayer for a
    // layer that MapLibre already has. MapLibre throws on duplicate source ids,
    // leaving stale raster tile templates in place. Treat add as an idempotent
    // replace so zoom/tilejson changes take effect after reloads or patches.
    this.removeMapLibreArtifacts(layer.id);

    // ---- VECTOR: GeoJSON / OGC Features URL ----
    if (layer.kind === 'vector' && layer.endpoints.features) {
      if (isNonJsonVectorFeaturesUrl(layer.endpoints.features)) {
        this.layerHealth.set(layer.id, 'unsupported');
        return;
      }
      try {
        await this.addVectorFromFeaturesEndpoint(sourceId, layerId, layer);
        this.layerHealth.set(layer.id, 'ready');
      } catch (err) {
        this.layerHealth.set(layer.id, 'failed');
        throw err;
      }
      return;
    }

    // ---- VECTOR PMTiles ----
    if (layer.kind === 'vector' && layer.endpoints.pmtiles) {
      try {
        ensurePmtilesProtocol();
        this.map.addSource(sourceId, {
          type: 'vector',
          url: `pmtiles://${layer.endpoints.pmtiles}`,
        });
        // Default style: outlined lines + soft fill. Real per-layer styling
        // arrives with the style editor in a later charter.
        const isContourDerivative = isContourPmtilesLayer(layer);
        const sourceLayer = await this.resolvePmtilesSourceLayer(layer, isContourDerivative);
        const color = (layer.style?.['color'] as string | undefined)
          ?? (isContourDerivative ? '#111827' : this.vectorFallback);
        console.info('[MapLibreAdapter] PMTiles mount', {
          layerId: layer.id,
          url: layer.endpoints.pmtiles,
          sourceLayer,
          color,
          bbox: layer.bbox,
          canvas: {
            width: this.map.getCanvas().clientWidth,
            height: this.map.getCanvas().clientHeight,
          },
        });
        if (!sourceLayer) {
          console.warn(
            `[MapLibreAdapter] PMTiles source layer missing for ${layer.id}; showing bbox fallback only`,
          );
          if (this.map.getSource(sourceId)) this.map.removeSource(sourceId);
          this.addVectorBboxFallback(sourceId, layerId, layer);
          this.layerHealth.set(layer.id, 'ready');
          return;
        }
        try {
          this.map.addLayer({
            id: `${layerId}-fill`,
            type: 'fill',
            source: sourceId,
            'source-layer': sourceLayer,
            minzoom: layer.visibility?.minZoom ?? 0,
            maxzoom: layer.visibility?.maxZoom ?? 22,
            filter: ['match', ['geometry-type'], ['Polygon', 'MultiPolygon'], true, false],
            paint: {
              'fill-color': color,
              'fill-opacity': isContourDerivative ? 0 : 0.24,
            },
          });
          this.registerVectorPickLayer(`${layerId}-fill`);
          this.map.addLayer({
            id: `${layerId}-line`,
            type: 'line',
            source: sourceId,
            'source-layer': sourceLayer,
            minzoom: layer.visibility?.minZoom ?? 0,
            maxzoom: layer.visibility?.maxZoom ?? 22,
            paint: {
              'line-color': color,
              'line-width': isContourDerivative ? 1 : 2,
              'line-opacity': isContourDerivative ? 0.85 : 1,
            },
          });
          this.registerVectorPickLayer(`${layerId}-line`);
          this.map.addLayer({
            id: `${layerId}-circle`,
            type: 'circle',
            source: sourceId,
            'source-layer': sourceLayer,
            minzoom: layer.visibility?.minZoom ?? 0,
            maxzoom: layer.visibility?.maxZoom ?? 22,
            filter: ['match', ['geometry-type'], ['Point', 'MultiPoint'], true, false],
            paint: {
              'circle-radius': 4,
              'circle-color': color,
              'circle-stroke-color': '#ffffff',
              'circle-stroke-width': 1,
            },
          });
          this.registerVectorPickLayer(`${layerId}-circle`);
          requestAnimationFrame(() => this.logVectorRenderState(`addLayer:${layer.id}`));
          this.fitBoundsForLayer(layer);
        } catch {
          if (this.map.getSource(sourceId)) this.map.removeSource(sourceId);
          this.addVectorBboxFallback(sourceId, layerId, layer);
        }
        this.layerHealth.set(layer.id, 'ready');
      } catch (err) {
        this.layerHealth.set(layer.id, 'failed');
        throw err;
      }
      return;
    }

    // ---- RASTER (XYZ template) ----
    if (layer.kind === 'raster' && layer.endpoints.tiles) {
      try {
        await this.addRasterTilesLayer(sourceId, layerId, layer);
        this.rasterLayerIds.add(layer.id);
        this.fitBoundsForLayer(layer);
        this.layerHealth.set(layer.id, 'ready');
      } catch (err) {
        this.layerHealth.set(layer.id, 'failed');
        throw err;
      }
      return;
    }

    // ---- VECTOR fallback: bbox rectangle (often invisible when bbox is global) ----
    if (layer.kind === 'vector' && layer.bbox) {
      try {
        this.addVectorBboxFallback(sourceId, layerId, layer);
        this.layerHealth.set(layer.id, 'ready');
      } catch (err) {
        this.layerHealth.set(layer.id, 'failed');
        throw err;
      }
      return;
    }

    // Unknown kind / no endpoints: ignore silently.
    this.layerHealth.set(layer.id, 'unsupported');
  }

  /**
   * Fetch `endpoints.features` (GeoJSON Feature( Collection ) or typical OGC API
   * Features JSON) and render fill + line layers.
   */
  private async addVectorFromFeaturesEndpoint(
    sourceId: string,
    layerId: string,
    layer: Layer,
  ): Promise<void> {
    if (!this.map) return;
    const url = layer.endpoints.features!;
    let data: Record<string, unknown>;
    try {
      const res = await fetch(url);
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      data = (await res.json()) as Record<string, unknown>;
    } catch (err) {
      console.warn('[MapLibreAdapter] features endpoint fetch failed', url, err);
      if (layer.bbox) this.addVectorBboxFallback(sourceId, layerId, layer);
      return;
    }

    let fc: BboxFeatureCollection;
    if (data.type === 'Feature') {
      fc = { type: 'FeatureCollection', features: [data] };
    } else if (data.type === 'FeatureCollection' && Array.isArray(data.features)) {
      fc = data as BboxFeatureCollection;
    } else if (Array.isArray((data as { features?: unknown }).features)) {
      // OGC API Features JSON often omits `type` on the root object.
      fc = {
        type: 'FeatureCollection',
        features: (data as { features: BboxFeatureCollection['features'] }).features,
      };
    } else {
      console.warn('[MapLibreAdapter] unexpected GeoJSON root type', data.type);
      if (layer.bbox) this.addVectorBboxFallback(sourceId, layerId, layer);
      return;
    }

    const color = (layer.style?.['color'] as string | undefined) ?? this.vectorFallback;

    const source: GeoJSONSourceSpecification = {
      type: 'geojson',
      data: fc as unknown as GeoJSON.FeatureCollection,
      // Promote `properties.id` so queryRenderedFeatures returns stable ids.
      promoteId: 'id',
    };

    this.map.addSource(sourceId, source);
    // Cache the features for WP-4 attribute-table consumption. The
    // structural shape (`Feature[]`) matches what `getVectorFeatures`
    // returns to the chrome.
    this.vectorFeatureCache.set(layer.id, fc.features as unknown as GeoJSON.Feature[]);

    this.map.addLayer({
      id: `${layerId}-fill`,
      type: 'fill',
      source: sourceId,
      filter: ['match', ['geometry-type'], ['Polygon', 'MultiPolygon'], true, false],
      paint: {
        'fill-color': color,
        'fill-opacity': 0.2,
      },
    });
    this.registerVectorPickLayer(`${layerId}-fill`);

    this.map.addLayer({
      id: `${layerId}-line`,
      type: 'line',
      source: sourceId,
      paint: {
        'line-color': color,
        'line-width': 2,
      },
    });
    this.registerVectorPickLayer(`${layerId}-line`);

    this.map.addLayer({
      id: `${layerId}-circle`,
      type: 'circle',
      source: sourceId,
      filter: ['match', ['geometry-type'], ['Point', 'MultiPoint'], true, false],
      paint: {
        'circle-color': color,
        'circle-radius': 6,
        'circle-stroke-color': color,
        'circle-stroke-width': 1,
      },
    });
    this.registerVectorPickLayer(`${layerId}-circle`);

    const bb = bboxFromGeoJsonFeatureCollection(fc);
    if (bb && this.map) {
      const [minX, minY, maxX, maxY] = bb;
      const lonSpan = maxX - minX;
      const latSpan = maxY - minY;
      // Skip auto-zoom for near-global extents (default scene bboxes).
      if (lonSpan > 0 && latSpan > 0 && lonSpan < 40 && latSpan < 40) {
        try {
          this.map.fitBounds(
            [
              [minX, minY],
              [maxX, maxY],
            ],
            { padding: 40, maxZoom: 15, duration: 600 },
          );
        } catch {
          /* ignore invalid bounds */
        }
      }
    }
  }

  async removeLayer(layerId: string): Promise<void> {
    if (!this.map) return;
    this.removeMapLibreArtifacts(layerId);
    this.rasterLayerIds.delete(layerId);
    this.vectorFeatureCache.delete(layerId);
    this.layerHealth.delete(layerId);
    this.pmtilesSourceLayerResolution.delete(layerId);
  }

  private removeMapLibreArtifacts(layerId: string): void {
    if (!this.map) return;
    const lid = `layer-${layerId}`;
    const lidLine = `${lid}-line`;
    const lidFill = `${lid}-fill`;
    const lidCircle = `${lid}-circle`;
    const lidLabel = `${lid}-label`;
    const sid = `src-${layerId}`;
    this.unregisterVectorPickLayer(lidFill);
    this.unregisterVectorPickLayer(lidLine);
    this.unregisterVectorPickLayer(lidCircle);
    this.unregisterVectorPickLayer(lid);
    if (this.map.getLayer(lidLabel)) this.map.removeLayer(lidLabel);
    if (this.map.getLayer(lidFill)) this.map.removeLayer(lidFill);
    if (this.map.getLayer(lidLine)) this.map.removeLayer(lidLine);
    if (this.map.getLayer(lidCircle)) this.map.removeLayer(lidCircle);
    if (this.map.getLayer(lid)) this.map.removeLayer(lid);
    if (this.map.getSource(sid)) this.map.removeSource(sid);
  }

  private async resolvePmtilesSourceLayer(
    layer: Layer,
    isContourDerivative: boolean,
  ): Promise<string | null> {
    const declared = readDeclaredPmtilesSourceLayer(layer);

    // Ground truth: the vector-layer ids the archive ACTUALLY contains. A
    // declared `source-layer` that is absent here renders nothing in MapLibre
    // (the classic "vector toggles but is blank"). Converters sometimes stamp a
    // base name while tippecanoe wrote a suffixed one (e.g. declared "Street",
    // archive "Streetintermediate"), so we reconcile rather than trust blindly.
    const available = await this.readPmtilesVectorLayerIds(layer.endpoints.pmtiles);

    const applied = this.reconcilePmtilesSourceLayer(declared, available, layer, isContourDerivative);
    this.pmtilesSourceLayerResolution.set(layer.id, { declared, applied, available });
    if (declared && applied && declared !== applied) {
      console.warn(
        `[MapLibreAdapter] source-layer "${declared}" not found in ${layer.id} archive; ` +
          `using "${applied}" instead (archive layers: ${available.join(', ') || 'unknown'}). ` +
          `Producer fix: stamp the archive's real source-layer name.`,
      );
    }
    return applied;
  }

  /**
   * Pure reconciliation of a declared PMTiles `source-layer` against the layer
   * ids actually present in the archive. Exposed (non-private) only for unit
   * tests; callers should use `resolvePmtilesSourceLayer`.
   *
   * Priority: exact archive match → fuzzy archive match (case-insensitive /
   * substring, to absorb tippecanoe suffixes) → archive's first layer → the
   * declared name (archive metadata unavailable) → kind heuristics → `features`.
   */
  reconcilePmtilesSourceLayer(
    declared: string | null,
    available: readonly string[],
    layer: Pick<Layer, 'id' | 'label'>,
    isContourDerivative: boolean,
  ): string | null {
    return reconcilePmtilesSourceLayer(declared, available, layer, isContourDerivative);
  }

  private async readPmtilesVectorLayerIds(url: string | undefined): Promise<string[]> {
    if (!url) return [];
    try {
      return [...(await readPmtilesArchiveInfo(url)).vectorLayerIds];
    } catch (err) {
      console.warn('[MapLibreAdapter] PMTiles metadata read failed', url, err);
      return [];
    }
  }

  getLayerHealth(layerId: string): 'ready' | 'loading' | 'failed' | 'unsupported' {
    return this.layerHealth.get(layerId) ?? 'loading';
  }

  /**
   * Live render diagnostics for the global Layer Properties console
   * (`LayerDiagnosticsProvider`). Returns `null` for layers this adapter has
   * never seen so the `ViewerHost` aggregator can fall through to another
   * adapter. MUST stay side-effect free.
   *
   * Layer-KIND aware only — branches off the MapLibre source/style-layer id
   * conventions, never a scene-specific layer id.
   */
  getLayerRenderDiagnostics(layerId: string): LayerRenderDiagnostics | null {
    // Health is set on the first `addLayer` and cleared on `removeLayer`, so it
    // is the cheapest "do I know this layer?" probe.
    if (!this.layerHealth.has(layerId)) return null;

    const sid = `src-${layerId}`;
    const lid = `layer-${layerId}`;
    const candidateLayerIds = [lid, `${lid}-fill`, `${lid}-line`, `${lid}-circle`, `${lid}-label`];

    const map = this.map;
    const presentLayerIds = map
      ? candidateLayerIds.filter((id) => Boolean(map.getLayer(id)))
      : [];
    const hasSource = Boolean(map?.getSource(sid));
    const mounted = presentLayerIds.length > 0 || hasSource || this.rasterLayerIds.has(layerId);

    // Ground-truth applied PMTiles `source-layer`, read straight off the live
    // style layer rather than re-deriving it from metadata.
    let selectedSourceLayer: string | null = null;
    if (map) {
      for (const id of [`${lid}-fill`, `${lid}-line`, `${lid}-circle`]) {
        const styleLayer = map.getLayer(id) as { sourceLayer?: string } | undefined;
        if (styleLayer?.sourceLayer) {
          selectedSourceLayer = styleLayer.sourceLayer;
          break;
        }
      }
    }

    // Producer-side source-layer reconciliation (declared vs applied vs archive)
    // captured at add time, so the Coordinates/Source tabs can flag a mismatch.
    const resolution = this.pmtilesSourceLayerResolution.get(layerId);
    if (resolution && !selectedSourceLayer) selectedSourceLayer = resolution.applied;

    return {
      adapterId: this.id,
      renderPath: 'maplibre-overlay',
      mounted,
      // MapLibre vectors/rasters toggle by mount/unmount, so there is no
      // in-place visibility to report. `null` keeps the builder's desync check
      // honest: it still flags "store visible but not mounted" via `mounted`.
      engineVisible: null,
      engineLayerIds: hasSource ? [sid, ...presentLayerIds] : presentLayerIds,
      frameCrs: 'EPSG:3857',
      alignmentStatus: 'not-applicable',
      selectedSourceLayer,
      declaredSourceLayer: resolution?.declared ?? null,
      availableSourceLayers: resolution?.available,
      health: this.layerHealth.get(layerId) ?? 'loading',
      lastError: null,
    };
  }

  flyTo(camera: ViewerCamera): void {
    this.map?.flyTo({
      center: camera.lonLat,
      zoom: camera.zoom ?? this.map.getZoom(),
    });
  }

  /**
   * MapLibre always renders in Web Mercator. As a vector OVERLAY it follows the
   * primary 3D camera and is NOT the frame authority, so it returns `null` then
   * to let the primary engine own the viewer-level frame readout.
   */
  getActiveFrameCrs(): string | null {
    return this.vectorOverlayMode ? null : 'EPSG:3857';
  }

  private logVectorRenderState(reason: string): void {
    if (!this.vectorOverlayMode || !this.map) return;
    try {
      const layers = [...this.vectorPickLayerIds].filter((id) => this.map?.getLayer(id));
      const features = layers.length > 0 ? this.map.queryRenderedFeatures(undefined, { layers }) : [];
      const center = this.map.getCenter();
      console.info('[MapLibreAdapter] overlay render state', {
        reason,
        center: [center.lng, center.lat],
        zoom: this.map.getZoom(),
        bearing: this.map.getBearing(),
        pitch: this.map.getPitch(),
        canvas: {
          width: this.map.getCanvas().clientWidth,
          height: this.map.getCanvas().clientHeight,
        },
        layers,
        renderedFeatureCount: features.length,
      });
    } catch (err) {
      console.warn('[MapLibreAdapter] overlay render-state probe failed', err);
    }
  }

  /**
   * Keep MapLibre layers mounted and flip their layout visibility in place.
   *
   * Without this hook `ViewerHost.setLayerVisible(false)` falls back to
   * `removeLayer`, deleting the host entry for PMTiles/vector overlays. A later
   * show request can then no-op because the host no longer has a mounted layer
   * entry. This is the generic "feature layer toggles but won't come back" bug.
   */
  setLayerVisible(layerId: string, visible: boolean): void {
    if (!this.map) return;
    const visibility = visible ? 'visible' : 'none';
    const candidates = [
      `layer-${layerId}`,
      `layer-${layerId}-fill`,
      `layer-${layerId}-line`,
      `layer-${layerId}-circle`,
      `layer-${layerId}-label`,
      `bbox-${layerId}`,
    ];
    for (const id of candidates) {
      try {
        if (this.map.getLayer(id)) {
          this.map.setLayoutProperty(id, 'visibility', visibility);
        }
      } catch (err) {
        console.warn('[MapLibreAdapter] setLayerVisible failed', id, err);
      }
    }
  }

  /**
   * Keep the vector OVERLAY aligned to the primary 3D camera.
   *
   * In `vectorOverlayMode` the authoritative camera lives on the primary engine
   * (Giro3D); MapLibre only paints vectors on a transparent, `pointer-events:none`
   * canvas above it and emits no `cameraChange` of its own. The shell calls this
   * on every primary-engine `cameraChange` so toggled-on PMTiles/GeoJSON vectors
   * actually render over the area in view instead of staying at the overlay's
   * mount-time default (the classic "vector toggles but nothing shows" bug).
   *
   * Because the overlay never receives pointer input and never emits camera
   * events, snapping it here can neither fight the user nor create a feedback
   * loop. Center always tracks. Zoom is taken from the event when it carries one
   * (discrete fly / zoom-to-layer); otherwise — free-orbit events from Giro3D
   * carry a camera `height` but no `zoom` — we derive a Web-Mercator zoom from
   * that height so the overlay scale tracks the 3D view. Without this the
   * overlay scale froze at its mount default and toggled-on vectors rendered at
   * the wrong scale, i.e. "vector toggles on but nothing shows in the main
   * viewer" even though the data is fine (the Preview map proves it). The
   * derivation is approximate (top-down overlay vs. a possibly-tilted 3D view,
   * assumed FOV); the goal is "vectors visible at roughly the right scale". The
   * overlay stays top-down (bearing/pitch untouched) by design.
   */
  syncOverlayCamera(camera: ViewerCamera): void {
    if (!this.vectorOverlayMode || !this.map) return;
    const lonLat = camera.lonLat;
    if (!Array.isArray(lonLat) || lonLat.length < 2) return;
    const [lon, lat] = lonLat;
    if (!Number.isFinite(lon) || !Number.isFinite(lat)) return;
    const opts: { center: [number, number]; zoom?: number; bearing?: number; pitch?: number } = {
      center: [lon, lat],
    };
    if (typeof camera.zoom === 'number' && Number.isFinite(camera.zoom)) {
      opts.zoom = Math.max(0, Math.min(22, camera.zoom));
    } else if (typeof camera.height === 'number' && Number.isFinite(camera.height)) {
      const viewportHeightPx =
        this.map.getCanvas()?.clientHeight
        || this.map.getContainer()?.clientHeight
        || 0;
      const derived = mercatorZoomForCameraHeight({
        heightMeters: camera.height,
        latDeg: lat,
        viewportHeightPx,
      });
      if (derived != null) opts.zoom = derived;
    }
    // Track the primary 3D camera's rotation/tilt so vector features orbit and
    // tilt WITH the Giro3D scene instead of staying locked north-up & top-down.
    // Giro3D emits hpr = [headingDeg, pitchDeg, rollDeg]:
    //   heading = atan2(east, north)  → clockwise-from-north compass bearing.
    //   pitch   = asin(dz / dist)     → 0 at the horizon, -90 looking straight down.
    // MapLibre: bearing = compass direction shown at top; pitch = 0 top-down → up
    // to maxPitch toward the horizon. So maplibrePitch ≈ 90 + giro3dPitch.
    const hpr = camera.hpr;
    if (Array.isArray(hpr) && hpr.length >= 2) {
      const headingDeg = hpr[0];
      const pitchDeg = hpr[1];
      if (Number.isFinite(headingDeg)) {
        // If rotation tracks the WRONG way for your build, flip this sign.
        opts.bearing = ((headingDeg % 360) + 360) % 360;
      }
      if (Number.isFinite(pitchDeg)) {
        const maxPitch = this.map.getMaxPitch?.() ?? 60;
        opts.pitch = Math.max(0, Math.min(maxPitch, 90 + pitchDeg));
      }
    }
    try {
      this.map.jumpTo(opts);
      requestAnimationFrame(() => this.logVectorRenderState('syncOverlayCamera'));
    } catch (err) {
      console.warn('[MapLibreAdapter] syncOverlayCamera failed', err);
    }
  }

  setTime(t: Date): void {
    void t;
    this.map?.triggerRepaint();
  }

  // ---------------------------------------------------------------------------
  // WP-4 — Vector ToolCard style hooks (thin pass-through; no behavior change)
  // ---------------------------------------------------------------------------
  //
  // The three setters below mutate paint / filter / layout properties on
  // the MapLibre layers we already create for vector sources in
  // `addLayer`. None of them changes existing add/remove semantics; they
  // are no-ops when the target layer is not present, so a Vector
  // ToolCard mounted before the layer is added will not throw.

  /**
   * Update stroke / fill paint on the vector layers added for `layerId`.
   * Accepts a partial delta so callers can update only what changed.
   */
  setVectorPaint(
    layerId: string,
    delta: {
      readonly fillColor?: string;
      readonly fillOpacity?: number;
      readonly lineColor?: string;
      readonly lineWidth?: number;
    },
  ): void {
    if (!this.map) return;
    const lineId = `layer-${layerId}-line`;
    const fillId = `layer-${layerId}-fill`;
    const singleId = `layer-${layerId}`;
    try {
      if (this.map.getLayer(lineId)) {
        if (delta.lineColor !== undefined)
          this.map.setPaintProperty(lineId, 'line-color', delta.lineColor);
        if (delta.lineWidth !== undefined)
          this.map.setPaintProperty(lineId, 'line-width', delta.lineWidth);
      } else if (this.map.getLayer(singleId)) {
        if (delta.lineColor !== undefined)
          this.map.setPaintProperty(singleId, 'line-color', delta.lineColor);
        if (delta.lineWidth !== undefined)
          this.map.setPaintProperty(singleId, 'line-width', delta.lineWidth);
      }
      if (this.map.getLayer(fillId)) {
        if (delta.fillColor !== undefined)
          this.map.setPaintProperty(fillId, 'fill-color', delta.fillColor);
        if (delta.fillOpacity !== undefined)
          this.map.setPaintProperty(fillId, 'fill-opacity', delta.fillOpacity);
      }
    } catch (err) {
      console.warn('[MapLibreAdapter] setVectorPaint failed', err);
    }
  }

  /**
   * Apply or clear a MapLibre filter expression on the vector layers
   * added for `layerId`. Passing `null` removes the filter.
   */
  setVectorFilter(layerId: string, filter: FilterSpecification | null): void {
    if (!this.map) return;
    const candidates = [
      `layer-${layerId}-line`,
      `layer-${layerId}-fill`,
      `layer-${layerId}`,
    ];
    for (const id of candidates) {
      try {
        if (this.map.getLayer(id)) {
          this.map.setFilter(id, filter);
        }
      } catch (err) {
        console.warn('[MapLibreAdapter] setVectorFilter failed', id, err);
      }
    }
  }

  /**
   * Toggle text labels on the vector layer. When enabled with a non-
   * null field, adds (idempotently) a `symbol` layer that reads
   * `['get', field]`; when disabled, removes the symbol layer.
   */
  setVectorLabels(
    layerId: string,
    options: { readonly enabled: boolean; readonly field: string | null },
  ): void {
    if (!this.map) return;
    const labelId = `layer-${layerId}-label`;
    const sourceId = `src-${layerId}`;
    try {
      if (!options.enabled || !options.field) {
        if (this.map.getLayer(labelId)) this.map.removeLayer(labelId);
        return;
      }
      if (!this.map.getSource(sourceId)) return;
      if (this.map.getLayer(labelId)) this.map.removeLayer(labelId);
      this.map.addLayer({
        id: labelId,
        type: 'symbol',
        source: sourceId,
        layout: {
          'text-field': ['to-string', ['get', options.field]],
          'text-size': 12,
          'text-anchor': 'top',
          'text-offset': [0, 0.5],
          'text-allow-overlap': false,
        },
        paint: {
          'text-color': this.canvasLight,
          'text-halo-color': this.vectorFallback,
          'text-halo-width': 1,
        },
      });
    } catch (err) {
      console.warn('[MapLibreAdapter] setVectorLabels failed', err);
    }
  }

  /**
   * Return the in-memory GeoJSON features held by the GeoJSON source
   * for `layerId`. Returns an empty array for non-GeoJSON sources
   * (PMTiles features are not queryable until they are visible at the
   * current viewport — out of scope for WP-4).
   */
  getVectorFeatures(layerId: string): ReadonlyArray<VectorExportFeature> {
    const features = this.vectorFeatureCache.get(layerId) ?? [];
    return features.map((feature) => {
      const props = (feature.properties ?? {}) as Record<string, unknown>;
      return {
        id: (feature.id ?? props.id) as string | number | undefined,
        properties: props as Readonly<Record<string, unknown>>,
        geometry: feature.geometry,
      };
    });
  }

  /**
   * G28 — pick the topmost rendered vector feature at a screen point.
   * Used by overlay-mode container clicks when MapLibre canvas is non-interactive.
   */
  pickVectorFeatureAtClientPoint(
    clientX: number,
    clientY: number,
  ): {
    readonly catalogLayerId: string;
    readonly featureId: string | number;
    readonly point: [number, number];
    readonly properties: Readonly<Record<string, unknown>>;
  } | null {
    if (!this.map) return null;
    const point = this.mapPointFromMouseEvent({ clientX, clientY } as MouseEvent);
    const lngLat = this.map.unproject(point);
    const hit = this.queryTopVectorFeature(point);
    if (!hit) return null;
    const props = this.lookupFeatureProperties(hit.catalogLayerId, hit.featureId);
    return {
      catalogLayerId: hit.catalogLayerId,
      featureId: hit.featureId,
      point: [lngLat.lng, lngLat.lat],
      properties: props,
    };
  }

  private registerVectorPickLayer(mapLayerId: string): void {
    this.vectorPickLayerIds.add(mapLayerId);
  }

  private unregisterVectorPickLayer(mapLayerId: string): void {
    this.vectorPickLayerIds.delete(mapLayerId);
  }

  private installVectorClickHandler(host: ViewerHostContext): void {
    if (this.containerClickHandler) return;
    this.containerClickHandler = (event: MouseEvent) => {
      if (!this.map) return;
      const point = this.mapPointFromMouseEvent(event);
      const hit = this.queryTopVectorFeature(point);
      if (hit) {
        const lngLat = this.map.unproject(point);
        host.emit({
          type: 'select',
          selection: {
            layerId: hit.catalogLayerId,
            featureId: hit.featureId,
            point: [lngLat.lng, lngLat.lat],
          },
        });
        return;
      }
      if (this.vectorOverlayMode) return;
      const canvas = this.map.getCanvas();
      const rect = canvas.getBoundingClientRect();
      if (
        event.clientX < rect.left
        || event.clientX > rect.right
        || event.clientY < rect.top
        || event.clientY > rect.bottom
      ) {
        return;
      }
      const lngLat = this.map.unproject(point);
      host.emit({
        type: 'select',
        selection: {
          layerId: '__map__',
          point: [lngLat.lng, lngLat.lat],
        },
      });
    };
    host.container.addEventListener('click', this.containerClickHandler, true);
  }

  private mapPointFromMouseEvent(event: MouseEvent): maplibregl.Point {
    const canvas = this.map!.getCanvas();
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return new maplibregl.Point(
      (event.clientX - rect.left) * scaleX,
      (event.clientY - rect.top) * scaleY,
    );
  }

  private queryTopVectorFeature(
    point: maplibregl.Point,
  ): {
    catalogLayerId: string;
    featureId: string | number;
    properties: Readonly<Record<string, unknown>>;
  } | null {
    if (!this.map || this.vectorPickLayerIds.size === 0) return null;
    const registeredLayers = [...this.vectorPickLayerIds].filter((id) => Boolean(this.map!.getLayer(id)));
    if (registeredLayers.length === 0) return null;

    const pickFromFeatures = (features: maplibregl.MapGeoJSONFeature[]) => {
      const ranked = [...features].sort((a, b) => {
        const rank = (layerId: string) => {
          if (layerId.includes('-fill')) return 0;
          if (layerId.includes('-circle')) return 1;
          if (layerId.includes('-line')) return 3;
          return 2;
        };
        return rank(a.layer.id) - rank(b.layer.id);
      });
      for (const feature of ranked) {
        if (!registeredLayers.includes(feature.layer.id)) continue;
        const catalogLayerId = catalogLayerIdFromMapLayerId(feature.layer.id);
        const featureId = resolveFeatureIdFromRenderedFeature(feature);
        if (catalogLayerId && featureId != null) {
          const rendered = { ...(feature.properties ?? {}) } as Record<string, unknown>;
          if (rendered.id == null) rendered.id = featureId;
          const cached = this.lookupFeatureProperties(catalogLayerId, featureId);
          const properties = { ...cached, ...rendered };
          this.lastVectorPickProperties = properties;
          return { catalogLayerId, featureId, properties };
        }
      }
      return null;
    };

    try {
      let features = this.map.queryRenderedFeatures(point, { layers: registeredLayers });
      const scopedHit = pickFromFeatures(features);
      if (scopedHit) return scopedHit;
      features = this.map.queryRenderedFeatures(point);
      return pickFromFeatures(features);
    } catch {
      return null;
    }
  }

  getLastVectorPickProperties(): Readonly<Record<string, unknown>> | null {
    return this.lastVectorPickProperties;
  }

  getFeatureProperties(
    layerId: string,
    featureId: string | number,
  ): Readonly<Record<string, unknown>> {
    return this.lookupFeatureProperties(layerId, featureId);
  }

  private lookupFeatureProperties(
    catalogLayerId: string,
    featureId: string | number,
  ): Readonly<Record<string, unknown>> {
    const wanted = String(featureId);
    const cached = this.getVectorFeatures(catalogLayerId).find((feature) => {
      const id = feature.id ?? feature.properties?.id ?? feature.properties?.gml_id;
      return id != null && String(id) === wanted;
    });
    return cached?.properties ?? {};
  }

  /**
   * Optional thin pass-through used by WP-7's
   * `GeophysicsToolCard` / `BathymetryToolCard` to drive the
   * `raster-opacity` paint property of a previously-added raster
   * layer. Adapter signatures elsewhere are intentionally unchanged
   * (per VIEWER-TOOLBAR-AND-GTWIN-FIRST-UX-MASTERPLAN.md §8.7
   * "thin pass-through, do not change signatures").
   */
  setRasterOpacity?(layerId: string, opacity: number): void {
    if (!this.map) return;
    if (!this.rasterLayerIds.has(layerId)) return;
    const lid = `layer-${layerId}`;
    if (!this.map.getLayer(lid)) return;
    const clamped = Math.max(0, Math.min(1, opacity));
    try {
      this.map.setPaintProperty(lid, 'raster-opacity', clamped);
    } catch {
      /* ignore — paint property may not be supported on this style */
    }
  }

  /**
   * Add a raster XYZ-template layer. When the URL is a TiTiler `/cog/tiles/`
   * endpoint, fetch the matching `tilejson.json` to use the COG's real bounds
   * + min/max zoom and request larger 512px tiles. This avoids 404s outside
   * the raster footprint and lets the user zoom to native pixel resolution.
   */
  private async addRasterTilesLayer(sourceId: string, layerId: string, layer: Layer): Promise<void> {
    if (!this.map) return;
    const baseTiles = normalizeRasterBands(normalizeTitilerSourceHost(layer.endpoints.tiles!), layer);
    let tiles: string = baseTiles;
    let tileSize = 256;
    let bounds: [number, number, number, number] | undefined;
    let minzoom = layer.visibility.minZoom;
    let maxzoom = layer.visibility.maxZoom;

    const tilejsonUrl = deriveTitilerTileJsonUrl(baseTiles, /* preferTileSize */ 512);
    if (tilejsonUrl) {
      try {
        const res = await fetch(tilejsonUrl);
        if (res.ok) {
          const tj = (await res.json()) as {
            tiles?: string[];
            bounds?: [number, number, number, number];
            minzoom?: number;
            maxzoom?: number;
          };
          if (Array.isArray(tj.tiles) && tj.tiles[0]) {
            tiles = tj.tiles[0];
            tileSize = tj.tiles[0].includes('tilesize=512') ? 512 : 256;
          }
          if (tj.bounds && tj.bounds.length === 4) bounds = tj.bounds;
          if (typeof tj.minzoom === 'number') minzoom = minzoom ?? tj.minzoom;
          if (typeof tj.maxzoom === 'number') maxzoom = maxzoom ?? tj.maxzoom;
        }
      } catch (err) {
        console.warn('[MapLibreAdapter] tilejson fetch failed; using template URL', err);
      }
    }

    console.info('[MapLibreAdapter] raster source configured', {
      layerId: layer.id,
      sourceId,
      tilejsonUrl,
      tileSize,
      minzoom,
      maxzoom,
      bounds,
      tiles,
    });

    if (
      bounds
      && bounds[0] <= -179.999
      && bounds[1] <= -89.999
      && bounds[2] >= 179.999
      && bounds[3] >= 89.999
    ) {
      console.warn('[MapLibreAdapter] skipping raster with invalid/global TiTiler bounds', layer.id, tiles);
      return;
    }

    this.map.addSource(sourceId, {
      type: 'raster',
      tiles: [tiles],
      tileSize,
      ...(bounds ? { bounds } : {}),
      ...(typeof minzoom === 'number' ? { minzoom } : {}),
      ...(typeof maxzoom === 'number' ? { maxzoom } : {}),
    });
    this.map.addLayer({
      id: layerId,
      type: 'raster',
      source: sourceId,
      minzoom: minzoom ?? 0,
      // Honour source / scene maxzoom. Otherwise a lower-resolution ortho
      // overzooms forever and masks higher-detail layers above it.
      maxzoom: maxzoom ?? 24,
      paint: {
        'raster-opacity': 1,
        'raster-resampling': 'linear',
      },
    });

    const logZoomVisibility = () => {
      const zoom = this.map?.getZoom();
      if (typeof zoom !== 'number') return;
      const effectiveMin = minzoom ?? 0;
      const effectiveMax = maxzoom ?? 24;
      const visibleAtZoom = zoom >= effectiveMin && zoom < effectiveMax;
      console.info('[MapLibreAdapter] raster zoom visibility', {
        layerId: layer.id,
        mapZoom: Number(zoom.toFixed(2)),
        visibleAtZoom,
        minzoom: effectiveMin,
        maxzoom: effectiveMax,
      });
    };
    logZoomVisibility();
    this.map.on('moveend', logZoomVisibility);

    const onSourceData = (event: { sourceId?: string; isSourceLoaded?: boolean; coord?: unknown }) => {
      if (event.sourceId !== sourceId) return;
      console.info('[MapLibreAdapter] raster source data', {
        layerId: layer.id,
        sourceId,
        mapZoom: this.map?.getZoom(),
        isSourceLoaded: event.isSourceLoaded,
        coord: event.coord,
      });
    };
    this.map.on('sourcedata', onSourceData);
  }

  /**
   * If the layer has a bounding box that is not the global default, fit the
   * map view to it so the user actually sees the data instead of a thumbnail.
   */
  private fitBoundsForLayer(layer: Layer): void {
    if (!this.map) return;
    // In overlay mode the camera is owned by the primary 3D engine and applied
    // every cameraChange via syncOverlayCamera. Animating fitBounds here fought
    // that sync (an 800ms tween yanking the overlay to the layer bbox, often the
    // whole region), so the vectors briefly flew off-view. Let the sync own it.
    if (this.vectorOverlayMode) return;
    if (!layer.bbox) return;
    const [minX, minY, maxX, maxY] = layer.bbox;
    if (
      ![minX, minY, maxX, maxY].every((n) => Number.isFinite(n)) ||
      maxX <= minX ||
      maxY <= minY
    ) {
      return;
    }
    const lonSpan = maxX - minX;
    const latSpan = maxY - minY;
    if (lonSpan >= 359 || latSpan >= 169) return;
    try {
      this.map.fitBounds(
        [
          [minX, minY],
          [maxX, maxY],
        ],
        { padding: 60, maxZoom: 18, duration: 800 },
      );
    } catch {
      /* ignore invalid bounds */
    }
  }

  private addVectorBboxFallback(sourceId: string, layerId: string, layer: Layer): void {
    if (!this.map) return;
    if (!layer.bbox) return;
    const [minX, minY, maxX, maxY] = layer.bbox;
    this.map.addSource(sourceId, {
      type: 'geojson',
      data: {
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'Polygon',
          coordinates: [[[minX, minY], [maxX, minY], [maxX, maxY], [minX, maxY], [minX, minY]]],
        },
      },
    });
    this.map.addLayer({
      id: layerId,
      type: 'line',
      source: sourceId,
      paint: { 'line-color': this.vectorFallback, 'line-width': 2 },
    });
    // Bbox placeholders have no feature ids — exclude from vector pick.
  }
}
